// TODO: Add validation of input control point positions

import Two from 'two.js';
import { clamp, lerp } from './math-utils';
import { BaseCurve } from './curves';
import EventEmitter from './EventEmitter';

const GRID_MARGIN = 32;
const GRID_POINT_PADDING = 32;
const CURVE_SEGMENTS = 256;
const CONTROL_POINT_RADIUS = 8;

type ChartBounds = {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export enum SongChartEvent {
  Update = 'update',
}

export type SongChartEventPayload = {
  curve: BaseCurve;
}

export default class SongChart extends
  EventEmitter<SongChart, SongChartEvent, SongChartEventPayload> {
  parent: HTMLElement;

  curve: BaseCurve;

  private context: Two;

  private pointerTarget: Two.Path|null;

  private pointerTargetStart: Two.Vector|null;

  private pointerOffsetX: number;

  private pointerOffsetY: number;

  private svgCurve!: Two.Path;

  private svgCurveControls: Two.Circle[];

  private svgCurveAnchors: Two.Anchor[];

  private activeControlIndex: number;

  private svgScrubber!: Two.Line;

  private svgBuffer!: Two.Line;

  constructor(parent: HTMLElement, curve: BaseCurve) {
    super();
    this.emitterContext = this;
    this.parent = parent;
    this.curve = curve;

    // Initialize drawing context
    const {
      top, left, bottom, right,
    } = this.parent.getBoundingClientRect();
    this.context = new Two({
      type: Two.Types.svg,
      width: right - left,
      height: bottom - top,
      autostart: true,
    }).appendTo(this.parent);

    this.pointerTarget = null;
    this.pointerTargetStart = null;
    this.pointerOffsetX = 0;
    this.pointerOffsetY = 0;
    // Shapes
    this.svgCurveControls = [];
    this.svgCurveAnchors = [];
    this.activeControlIndex = 0;

    // Draw chart
    this.initChart();
    // Draw curve
    this.initCurve();

    this.context.update();

    // Initialize events
    window.addEventListener('pointermove', this.onPointerMove.bind(this));
    window.addEventListener('pointerup', this.onPointerRelease.bind(this));
    this.parent.addEventListener('pointerup', this.onPointerRelease.bind(this));
  }

  setBuffer(t: number) {
    const { width } = this.context;
    this.svgBuffer.translation.x = lerp(0, width - (GRID_MARGIN * 2), t);
  }

  setProgress(t: number) {
    const { width } = this.context;
    this.svgScrubber.translation.x = lerp(0, width - (GRID_MARGIN * 2), t);
  }

  private initChart() {
    const { width, height } = this.context;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Initialize grid
    const gridWidth = width - (GRID_MARGIN * 2);
    const gridHeight = height - (GRID_MARGIN * 2);
    const background = this.context.makeRectangle(halfWidth, halfHeight, width, height);
    background.fill = 'black';
    const grid = this.context.makeRectangle(halfWidth, halfHeight, gridWidth, gridHeight);
    grid.fill = 'black';
    grid.linewidth = 2;
    grid.stroke = '#333333';
    // Add baseline
    const baseline = this.context.makeLine(
      GRID_MARGIN,
      halfHeight,
      gridWidth + GRID_MARGIN,
      halfHeight,
    );
    baseline.linewidth = 2;
    baseline.stroke = '#333333';
    // Add buffering indicator
    this.svgBuffer = this.context.makeLine(
      GRID_MARGIN,
      GRID_MARGIN,
      GRID_MARGIN,
      height - GRID_MARGIN,
    );
    this.svgBuffer.linewidth = 2;
    this.svgBuffer.stroke = '#333333';
    // Draw playback scrubber
    this.svgScrubber = this.context.makeLine(
      GRID_MARGIN,
      GRID_MARGIN,
      GRID_MARGIN,
      height - GRID_MARGIN,
    );
    this.svgScrubber.linewidth = 2;
    this.svgScrubber.stroke = '#F5DA42';
  }

  private initCurve() {
    const svgCurveGroup = this.context.makeGroup();
    // Add evenly spaced out points along curve
    this.svgCurveAnchors = [];
    for (let i = 0; i <= CURVE_SEGMENTS; i += 1) {
      const t = i / CURVE_SEGMENTS;
      const position = this.controlPointToCanvasPosition([
        t,
        this.curve.getValue(t),
      ]);
      this.svgCurveAnchors.push(new Two.Anchor(
        position.x,
        position.y,
        0, 0, 0, 0,
        Two.Commands.line,
      ));
    }
    this.svgCurve = this.context.makeCurve(this.svgCurveAnchors, true);
    this.svgCurve.noFill();
    this.svgCurve.stroke = 'white';
    this.svgCurve.linewidth = 2;
    this.svgCurve.cap = 'round';
    svgCurveGroup.add(this.svgCurve);

    this.curve.controls.forEach((controlPoint, index) => {
      const anchorPosition = this.controlPointToCanvasPosition(controlPoint);
      const control = this.context.makeCircle(
        anchorPosition.x,
        anchorPosition.y,
        CONTROL_POINT_RADIUS,
      );
      svgCurveGroup.add(control);
      control.fill = 'white';
      control.linewidth = 5;
      control.stroke = 'black';
      // Create elements before binding event handlers
      this.context.update();
      // https://stackoverflow.com/questions/29812960/how-to-capture-a-click-event-on-a-shape-or-group-in-two-js
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      // eslint-disable-next-line no-underscore-dangle
      const controlRenderer: SVGElement = control._renderer.elem;
      controlRenderer.style.cursor = 'grab';
      controlRenderer.addEventListener('pointerdown', () => {
        controlRenderer.style.cursor = 'grabbing';
        this.pointerTarget = control;
        this.pointerTargetStart = control.translation.clone();
        this.activeControlIndex = index;
      });
      controlRenderer.addEventListener('pointerup', () => {
        controlRenderer.style.cursor = 'grab';
      });
      this.svgCurveControls.push(control);
    });
  }

  private updateCurve() {
    this.svgCurveAnchors.forEach((anchor) => {
      const position = new Two.Vector(
        anchor.x + this.svgCurve.translation.x,
        anchor.y + this.svgCurve.translation.y,
      );
      const [x] = this.canvasPositionToControlPoint(position);
      const y = this.curve.getValue(x);
      // eslint-disable-next-line no-param-reassign
      anchor.y = this.controlPointToCanvasPosition([x, y]).y - this.svgCurve.translation.y;
    });
    this.emit(SongChartEvent.Update, {
      curve: this.curve.clone(),
    });
  }

  private controlPointToCanvasPosition(controlPoint: number[]): Two.Vector {
    const { width, height } = this.context;
    const gridWidth = width - (GRID_MARGIN * 2);
    const gridHeight = height - (GRID_MARGIN * 2);
    const [x, y] = controlPoint;
    const clampedX = clamp(x, 0, 1);
    const clampedY = clamp(y, 0, 1);
    return new Two.Vector(
      GRID_MARGIN + clampedX * gridWidth,
      GRID_MARGIN + (1 - clampedY) * gridHeight,
    );
  }

  private canvasPositionToControlPoint(canvasPosition: Two.Vector): number[] {
    const { width, height } = this.context;
    const gridWidth = width - (GRID_MARGIN * 2);
    const gridHeight = height - (GRID_MARGIN * 2);
    return [
      (canvasPosition.x - GRID_MARGIN) / gridWidth,
      1 - (canvasPosition.y - GRID_MARGIN) / gridHeight,
    ];
  }

  private getControlPointBounds(controlPointIndex: number): ChartBounds {
    const { width, height } = this.context;
    const control = this.svgCurveControls[controlPointIndex];
    let left = 0;
    let right = 0;
    // If first/last control point, restrict all horizonatal movement
    if (controlPointIndex === 0 || controlPointIndex === this.curve.controls.length - 1) {
      left = control.translation.x;
      right = control.translation.x;
    } else {
      const previousAnchor = this.svgCurveControls[controlPointIndex - 1];
      const nextAnchor = (this.svgCurveControls.length > controlPointIndex + 1)
        ? this.svgCurveControls[controlPointIndex + 1]
        : null;
      left = Math.max(
        GRID_MARGIN,
        previousAnchor ? previousAnchor.translation.x + GRID_POINT_PADDING : 0,
      );
      right = Math.min(
        width - GRID_MARGIN,
        nextAnchor ? nextAnchor.translation.x - GRID_POINT_PADDING : width,
      );
    }
    return {
      top: GRID_MARGIN,
      bottom: height - GRID_MARGIN,
      left,
      right,
    };
  }

  private resetPointerMeta() {
    this.pointerTarget = null;
    this.pointerOffsetX = 0;
    this.pointerOffsetY = 0;
  }

  private onPointerMove(event: PointerEvent) {
    if (this.pointerTarget && this.pointerTargetStart) {
      this.pointerOffsetX += event.movementX;
      this.pointerOffsetY += event.movementY;
      const {
        top, bottom, left, right,
      } = this.getControlPointBounds(this.activeControlIndex);
      const clampedOffsetX = clamp(this.pointerTargetStart.x + this.pointerOffsetX, left, right);
      const clampedOffsetY = clamp(this.pointerTargetStart.y + this.pointerOffsetY, top, bottom);
      this.pointerTarget.translation.set(clampedOffsetX, clampedOffsetY);
      this.svgCurveControls.forEach((control, index) => {
        this.curve.controls[index] = this.canvasPositionToControlPoint(control.translation);
      });
      this.updateCurve();
    }
  }

  private onPointerRelease() {
    this.resetPointerMeta();
    this.activeControlIndex = 0;
  }
}
